import { QueryClient } from "@tanstack/react-query";
import {
  createTRPCClient,
  createWSClient,
  httpBatchLink,
  wsLink,
} from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import type {
  WebAppRouter,
  RealtimeRouter,
} from "../../NativeApp/Backend/main/src/index";
import { auth } from "./firebase";

const url = `https://beta.flateroo.com/webapp`;
const devHost =
  typeof window !== "undefined" ? window.location.hostname : "localhost";
/* const url =
  process.env.NODE_ENV === "development"
    ? `http://${devHost}:3000/webapp`
    : `https://beta.flateroo.com/webapp`; */
//const url = `https://dev.flateroo.com/webapp`;

export const wsUrl = `wss://beta.flateroo.com/realtime`;
/* export const wsUrl =
  process.env.NODE_ENV === "development"
    ? `ws://${devHost}:3000/realtime`
    : `wss://beta.flateroo.com/realtime`; */
//export const wsUrl = `wss://dev.flateroo.com/realtime`;

export const trpcReact = createTRPCReact<WebAppRouter>();

export const queryClient = new QueryClient();

// Turnstile token management – set before a protected mutation, cleared after
let pendingTurnstileToken: string | null = null;

export function setTurnstileToken(token: string | null) {
  pendingTurnstileToken = token;
}

export function clearTurnstileToken() {
  pendingTurnstileToken = null;
}

// Function to get auth token
const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch (error) {
    console.error("Error getting auth token:", error);
    return null;
  }
};

export const trpcClient = trpcReact.createClient({
  links: [
    httpBatchLink({
      url: url,
      async headers() {
        const headers: Record<string, string> = {};
        const token = await getAuthToken();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        if (pendingTurnstileToken) {
          headers["x-turnstile-token"] = pendingTurnstileToken;
        }
        return headers;
      },
    }),
  ],
});

// Realtime WebSocket client for subscriptions
class RealtimeClient {
  private isAuthenticated: boolean;
  private client: ReturnType<typeof createTRPCClient<RealtimeRouter>> | null;
  private wsClient: ReturnType<typeof createWSClient> | null;
  private connectionListeners: Map<number, (value: unknown) => void>;
  private listenerCounter: number;

  constructor() {
    this.isAuthenticated = false;
    this.wsClient = null;
    this.client = null;
    this.connectionListeners = new Map();
    this.listenerCounter = 0;
  }

  destroy() {
    this.client = null;
    this.wsClient?.close();
    this.wsClient = null;
    this.connectionListeners.clear();
  }

  private async waitForToken(): Promise<string> {
    let token = await getAuthToken();
    let retryCount = 0;
    const maxRetries = 2;
    const baseDelay = 1000;

    while (!token && retryCount < maxRetries) {
      const delay = baseDelay * Math.pow(2, retryCount);
      await new Promise<void>((resolve) => setTimeout(resolve, delay));
      token = await getAuthToken();
      retryCount++;
    }

    if (!token) {
      throw new Error("Failed to retrieve token after multiple retries.");
    }

    return token;
  }

  public addConnectionListener(listener: (value: unknown) => void): number {
    const id = this.listenerCounter++;
    this.connectionListeners.set(id, listener);
    return id;
  }

  public removeConnectionListener(id: number) {
    this.connectionListeners.delete(id);
  }

  public getClient(): ReturnType<
    typeof createTRPCClient<RealtimeRouter>
  > | null {
    if (!this.isAuthenticated) return null;
    if (!this.client) {
      console.log("Creating new WebSocket client for webapp");
      this.wsClient = createWSClient({
        url: wsUrl,
        lazy: { enabled: false, closeMs: 0 },
        retryDelayMs: (retries) => {
          const base = Math.min(30000, 1000 * Math.pow(2, retries));
          const jitter = Math.floor(Math.random() * 500);
          return base + jitter;
        },
        connectionParams: async () => {
          if (!this.isAuthenticated) return null;
          return {
            token: await this.waitForToken(),
            version: "webapp-1.0.0",
            isWebApp: "true",
          };
        },
      });

      this.wsClient.connectionState.subscribe({
        next: (value: unknown) => {
          this.connectionListeners.forEach((listener) => {
            listener(value);
          });
        },
      });

      this.client = createTRPCClient<RealtimeRouter>({
        links: [
          wsLink({
            client: this.wsClient,
          }),
        ],
      });
    }
    return this.client;
  }

  public setIsAuthenticated(isAuthenticated: boolean) {
    this.isAuthenticated = isAuthenticated;
    if (isAuthenticated) {
      this.getClient();
    } else {
      this.client = null;
      this.wsClient?.close();
      this.wsClient = null;
    }
  }
}

export const realtimeClient = new RealtimeClient();
